// File: src/components/pages/payments/PaymentMappingTab.jsx
// Two-column mapping UI: select a Confirmed payment + Pending classes → map

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Sparkles, AlertTriangle, CheckCircle2, User, Calendar,
  CreditCard, Link2, RefreshCw, Clock, AlertCircle
} from 'lucide-react';
import { paymentAPI, bookingHistoryAPI, studentAPI } from '../../../services/api';
import '../../../styles/unified.css';

const fmt = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const durationHours = (booking) => {
  try {
    const parse = t => { const s = t.replace(':',''); return parseInt(s.slice(0,2))*60+parseInt(s.slice(2,4)); };
    return parseFloat(((parse(booking.endTime) - parse(booking.startTime)) / 60).toFixed(2));
  } catch { return null; }
};

const PaymentMappingTab = ({ onMappingComplete }) => {
  const { user, getAccessTokenSilently } = useAuth0();

  const [students,         setStudents]         = useState([]);
  const [selectedStudentId,setSelectedStudentId]= useState('');
  const [payments,         setPayments]         = useState([]);
  const [pendingClasses,   setPendingClasses]   = useState([]);
  const [selectedPayment,  setSelectedPayment]  = useState(null);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [loadingClasses,   setLoadingClasses]   = useState(false);
  const [mapping,          setMapping]          = useState(false);
  const [error,            setError]            = useState(null);
  const [message,          setMessage]          = useState(null);

  // Load active students for dropdown
  useEffect(() => {
    studentAPI.getStudentsByStatus('active', getAccessTokenSilently)
      .then(r => setStudents(r.data || []))
      .catch(console.error);
  }, [getAccessTokenSilently]);

  // Load Confirmed payments when student changes
  const loadPayments = useCallback(async () => {
    if (!selectedStudentId) { setPayments([]); return; }
    setLoading(true); setError(null);
    try {
      const res = await paymentAPI.getConfirmedPayments(getAccessTokenSilently);
      const studentPayments = (res.data || []).filter(p => p.studentId === selectedStudentId);
      setPayments(studentPayments);
      setSelectedPayment(null);
      setSelectedClassIds([]);
      setPendingClasses([]);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [selectedStudentId, getAccessTokenSilently]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  // Load pending classes when student changes
  useEffect(() => {
    if (!selectedStudentId) { setPendingClasses([]); return; }
    setLoadingClasses(true);
    bookingHistoryAPI.getPendingClasses(selectedStudentId, getAccessTokenSilently)
      .then(r => setPendingClasses(r.data || []))
      .catch(e => setError(e.message))
      .finally(() => setLoadingClasses(false));
  }, [selectedStudentId, getAccessTokenSilently]);

  // ── Mismatch detection ───────────────────────────────────────────────────────
  const mismatches = (selectedPayment && pendingClasses?.length > 0)
    ? pendingClasses
        .filter(c => selectedClassIds.includes(c.bookingId))
        .filter(c => {
          const d = durationHours(c);
          return d !== null && selectedPayment.lessonDuration && d !== selectedPayment.lessonDuration;
        })
    : [];
  // ── Map handler ──────────────────────────────────────────────────────────────
  const handleMap = async () => {
    if (!selectedPayment) { setError('Please select a payment first.'); return; }
    if (!pendingClasses || pendingClasses.length === 0) { setError('No pending classes available to map.'); return; }
    if (selectedClassIds.length === 0) { setError('Please select at least one class.'); return; }
    if (selectedClassIds.length !== selectedPayment.numberOfClasses) {
      setError(`Please select exactly ${selectedPayment.numberOfClasses} class${selectedPayment.numberOfClasses !== 1 ? 'es' : ''}`);
      return;
    }
    if (mismatches.length > 0) {
      if (!window.confirm(
        `${mismatches.length} selected class${mismatches.length !== 1 ? 'es have' : ' has'} a duration mismatch with this payment.\n\nPayment duration: ${selectedPayment.lessonDuration}h\n\nContinue anyway?`
      )) return;
    }

    setMapping(true); setError(null);
    try {
      const res = await bookingHistoryAPI.mapPaymentToClasses({
        paymentId:   selectedPayment.paymentId,
        bookingIds:  selectedClassIds,
        instructorId: user?.sub
      }, getAccessTokenSilently);

      setMessage({ text: `Mapped ${res.data.classesMapped} class${res.data.classesMapped !== 1 ? 'es' : ''} successfully.`, type: 'success' });
      setTimeout(() => setMessage(null), 4000);
      setSelectedPayment(null);
      setSelectedClassIds([]);
      await loadPayments();
      onMappingComplete?.();
    } catch (e) {
      setError(e.message);
    } finally { setMapping(false); }
  };

  const toggleClass = (bookingId) =>
    setSelectedClassIds(prev =>
      prev.includes(bookingId) ? prev.filter(id => id !== bookingId) : [...prev, bookingId]
    );

  const canMap = selectedPayment && selectedPayment.numberOfClasses > 0 && selectedClassIds.length === selectedPayment.numberOfClasses && pendingClasses?.length > 0;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Toast */}
      {message && (
        <div className={`save-message ${message.type} save-message-dismissible`}>
          <span className="save-message-text">{message.text}</span>
          <button className="save-message-close" onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {/* Student selector */}
      <div className="card">
        <div className="card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <User size={18} color="#667eea" />
            <label style={{ fontWeight: 600, fontSize: '0.95rem' }}>Select Student</label>
            <select
              value={selectedStudentId}
              onChange={e => setSelectedStudentId(e.target.value)}
              className="form-select"
              style={{ minWidth: '220px' }}
            >
              <option value="">— Choose a student —</option>
              {students.map(s => (
                <option key={s.studentId} value={s.studentId}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </select>
            <button onClick={loadPayments} className="btn-secondary" title="Refresh" disabled={loading}>
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}

      {/* Two-column mapping area */}
      {selectedStudentId && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>

          {/* ── Left: Confirmed Payments ── */}
          <div>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CreditCard size={16} color="#667eea" /> Confirmed Payments
              {(payments?.length ?? 0) > 0 && (
                <span style={{ fontSize: '11px', background: '#ede9fe', color: '#5b21b6', padding: '1px 7px', borderRadius: '99px', fontWeight: 700 }}>
                  {payments.length}
                </span>
              )}
            </h4>

            {loading ? (
              <div className="loading-state" style={{ padding: '2rem' }}><div className="spinner" /></div>
            ) : !payments || payments.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px dashed #e2e8f0' }}>
                {selectedStudentId ? 'No confirmed payments for this student' : 'Select a student to view payments'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {payments.map(p => (
                  <label
                    key={p.paymentId}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                      padding: '0.875rem', cursor: 'pointer',
                      background: selectedPayment?.paymentId === p.paymentId ? '#f5f3ff' : '#fff',
                      border: `1.5px solid ${selectedPayment?.paymentId === p.paymentId ? '#7c3aed' : '#e5e7eb'}`,
                      borderRadius: '0.5rem', transition: 'all 0.15s'
                    }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={p.paymentId}
                      checked={selectedPayment?.paymentId === p.paymentId}
                      onChange={() => {
                        setSelectedPayment(p);
                        // Preselect first N classes where duration matches (no mismatch)
                        const matching = pendingClasses
                          .filter(c => {
                            const d = durationHours(c);
                            return d !== null && d === p.lessonDuration;
                          })
                          .slice(0, p.numberOfClasses)
                          .map(c => c.bookingId);
                        // If fewer matching than needed, fall back to first N regardless
                        const preselected = matching.length === p.numberOfClasses
                          ? matching
                          : pendingClasses.slice(0, p.numberOfClasses).map(c => c.bookingId);
                        setSelectedClassIds(preselected);
                      }}
                      style={{ marginTop: '2px', accentColor: '#7c3aed' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: '0.25rem' }}>
                        {p.paymentOption} — £{p.paymentAmount?.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <span><Calendar size={11} style={{ verticalAlign: '-1px' }} /> {fmt(p.paymentDate)}</span>
                        <span>{p.numberOfClasses} class{p.numberOfClasses !== 1 ? 'es' : ''}</span>
                        {p.lessonDuration && <span><Clock size={11} style={{ verticalAlign: '-1px' }} /> {p.lessonDuration}h/class</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Pending Classes ── */}
          <div>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={16} color="#10b981" /> Pending Classes
              {(pendingClasses?.length ?? 0) > 0 && (
                <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '1px 7px', borderRadius: '99px', fontWeight: 700 }}>
                  {pendingClasses.length}
                </span>
              )}
              {selectedPayment && (
                <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: 'auto' }}>
                  Select {selectedPayment.numberOfClasses}
                </span>
              )}
            </h4>

            {loadingClasses ? (
              <div className="loading-state" style={{ padding: '2rem' }}><div className="spinner" /></div>
            ) : !pendingClasses || pendingClasses.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px dashed #e2e8f0' }}>
                {selectedStudentId ? 'No pending classes for this student' : 'Select a student to view pending classes'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {pendingClasses.map(cls => {
                  const dur        = durationHours(cls);
                  const isSelected = selectedClassIds.includes(cls.bookingId);
                  const isMismatch = selectedPayment && dur !== null && dur !== selectedPayment.lessonDuration;
                  return (
                    <label
                      key={cls.bookingId}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                        padding: '0.875rem', cursor: selectedPayment ? 'pointer' : 'default',
                        background: isSelected ? '#f0fdf4' : isMismatch ? '#fffbeb' : '#fff',
                        border: `1.5px solid ${isSelected ? '#10b981' : isMismatch ? '#f59e0b' : '#e5e7eb'}`,
                        borderRadius: '0.5rem', transition: 'all 0.15s',
                        opacity: !selectedPayment ? 0.5 : 1
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!selectedPayment}
                        onChange={() => toggleClass(cls.bookingId)}
                        style={{ marginTop: '2px', accentColor: '#10b981' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {fmt(cls.date)}
                          {isMismatch && (
                            <span style={{ fontSize: '10px', background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: '99px', fontWeight: 700 }}>
                              <AlertTriangle size={9} style={{ verticalAlign: '-1px' }} /> Duration mismatch
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span>{fmtTime(cls.startTime)} – {fmtTime(cls.endTime)}</span>
                          {dur && <span><Clock size={11} style={{ verticalAlign: '-1px' }} /> {dur}h</span>}
                        </div>
                      </div>
                      {isSelected && <CheckCircle2 size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mismatch warning */}
      {mismatches.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', padding: '0.875rem' }}>
          <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <strong style={{ fontSize: '0.88rem', color: '#92400e' }}>Duration mismatch on {mismatches.length} selected class{mismatches.length !== 1 ? 'es' : ''}</strong>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#b45309' }}>
              Payment expects {selectedPayment?.lessonDuration}h sessions. You can still proceed — you will be asked to confirm.
            </p>
          </div>
        </div>
      )}

      {/* Map button */}
      {selectedPayment && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleMap}
            disabled={!canMap || mapping}
            className="btn-success"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '180px', justifyContent: 'center', opacity: !canMap ? 0.5 : 1 }}
          >
            <Link2 size={18} />
            {mapping ? 'Mapping…' : `Map Payment → ${selectedClassIds.length}/${selectedPayment.numberOfClasses} Classes`}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentMappingTab;